import React, { use, useEffect, useMemo, useState } from 'react';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import { DateCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { SubjectAssociationTypes } from '@interfaces-clean/subject-monitoring/subject-observations';
import { SubjectReviewNoteDto } from '@interfaces-clean/subject-monitoring.interface';
import AddSubjectNoteDialog from '@pages-clean/subject-monitoring/components/dialogs/add-subject-note-dialog';
import EditSmObservationDialog from '@pages-clean/subject-monitoring/components/dialogs/edit-sm-observation-dialog';
import EditSubjectNoteDialog from '@pages-clean/subject-monitoring/components/dialogs/edit-subject-note-dialog';
import { mapNoteToObservationGridRow } from '@pages-clean/subject-monitoring/components/dialogs/helpers';
import { SMActiveReviewContext } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab/context';
import SubjectMonitoringService from '@pages-clean/subject-monitoring/subject-hub/subject-monitoring.service';
import SubjectProfileService from '@pages-clean/subject-monitoring/subject-profile/subject-profile-service';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import { SubjectNotesApi } from '@apis/bff-api/subject-monitoring/subject-notes-api';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type SubjectMonitoringNotesGridProps = {
    subjectId: number;
    subjectNumber?: string;
    studyRef: string;
    canEdit: boolean;
    datasetId: number;
    datasetOpenedOn: Date;
};

const defaultColumnSize = { width: 1, minWidth: 140 };

const SubjectMonitoringNotesGrid = ({
    subjectId,
    subjectNumber,
    studyRef,
    canEdit,
    datasetId,
    datasetOpenedOn,
}: SubjectMonitoringNotesGridProps) => {
    const { t: tSm } = useTranslation('sm', {
        keyPrefix: 'subjectProfile.notesTab',
    });
    const { t: tCommon } = useTranslation('common');
    const { TooltipCell } = createGridCustomCells();
    const [showAddNoteDialog, setShowAddNoteDialog] = useState(false);
    const [noteToEdit, setNoteToEdit] = useState<SubjectReviewNoteDto | undefined>();
    const [showEditNoteDialog, setShowEditNoteDialog] = useState(false);
    const [showConvertDialog, setShowConvertDialog] = useState(false);

    const getAllNotesQuery = SubjectNotesApi.useGetAllNotesQuery(studyRef, subjectId);
    const { data: cm2Datasets } = DatasetApi.useGetActive(studyRef);

    const smActiveReviewContext = use(SMActiveReviewContext);
    const reviewRecordId = smActiveReviewContext?.state?.reviewRecordId;
    const sessionId = smActiveReviewContext?.state?.sessionId;

    const getReviewQuery = SubjectMonitoringReviewAPI.useGetByRecordIdQuery(studyRef, reviewRecordId);
    const activeReview = getReviewQuery.data;

    const columns = useMemo(() => {
        if (!cm2Datasets) {
            return [];
        }

        const tooltipCellTwoLine = TooltipCell({
            isCsv: false,
            maxLines: 2,
        });

        const datasetDates = [
            ...new Set(cm2Datasets?.map((x) => stringDateWithoutTimeFromUtc(x.dataCutDate!, 'DD-MMM-YYYY')!)),
        ].sort((a, b) => (new Date(a) > new Date(b) ? -1 : 1));

        const cols: GridColumnProps[] = [
            {
                id: 'noteText',
                field: 'noteText',
                title: tSm('grid.noteText'),
                width: 1,
                minWidth: 250,
                locked: true,
                lockedByDefault: true,
                cells: { data: tooltipCellTwoLine },
            },
            {
                id: 'observationDate',
                field: 'observationDate',
                title: tSm('grid.observationDate'),
                cells: { data: DateCell },
                locked: true,
                lockedByDefault: true,
                filter: 'date',
                ...defaultColumnSize,
            },
            {
                id: 'datasetOpenedOn',
                field: 'datasetOpenedOn',
                title: tSm('grid.datasetOpenedOn'),
                lovData: datasetDates,
                ...defaultColumnSize,
            },
            {
                id: 'association',
                field: 'association',
                title: tSm('grid.association'),
                lovData: ['Subject Hub', 'Subject Profile'],
                ...defaultColumnSize,
            },
            {
                id: 'dataListing',
                field: 'dataListing',
                title: tSm('grid.dataListing'),
                ...defaultColumnSize,
            },
            {
                id: 'createdByName',
                field: 'createdByName',
                title: tSm('grid.createdByName'),
                cells: { data: tooltipCellTwoLine },
                ...defaultColumnSize,
            },
            {
                id: 'created',
                field: 'created',
                title: tSm('grid.created'),
                cells: { data: DateCell },
                filter: 'date',
                ...defaultColumnSize,
            },
            {
                id: 'updatedByName',
                field: 'updatedByName',
                title: tSm('grid.updatedByName'),
                cells: { data: tooltipCellTwoLine },
                ...defaultColumnSize,
            },
            {
                id: 'updatedDate',
                field: 'updatedDate',
                title: tSm('grid.updatedDate'),
                cells: { data: DateCell },
                filter: 'date',
                ...defaultColumnSize,
            },

            {
                id: 'observationId',
                field: 'id',
                title: tSm('grid.observationId'),
                showByDefault: false,
                ...defaultColumnSize,
            },
            {
                id: 'subject',
                field: 'subjectNumber',
                title: tSm('grid.subjectNumber'),
                showByDefault: false,
                ...defaultColumnSize,
            },
        ];
        return cols;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [cm2Datasets]);

    const noteMenuActions: RowMenuAction[] = useMemo(
        () => [
            {
                id: 1,
                label: tSm('editNote'),
                onClick: (dataItem: unknown) => {
                    setNoteToEdit(dataItem as SubjectReviewNoteDto);
                    setShowEditNoteDialog(true);
                },
                icon: ['far', 'pen-to-square'],
                disabled: !canEdit,
            },
        ],
        [canEdit, tSm],
    );

    const onEditNoteClose = () => {
        setShowEditNoteDialog(false);
        setNoteToEdit(undefined);
    };

    const onEditNoteSubmit = () => {
        getAllNotesQuery.mutate().catch((err) => window.console.log(err));
        setShowEditNoteDialog(false);
        setNoteToEdit(undefined);
    };

    const onConvertToObservation = (observationText: string) => {
        setNoteToEdit((prevValue) => ({ ...prevValue, noteText: observationText }));
        setShowEditNoteDialog(false);
        setShowConvertDialog(true);
    };

    const onConvertClose = () => {
        setShowConvertDialog(false);
        setNoteToEdit(undefined);
    };

    const onConvertSubmit = (id: number) => {
        if (id > 0) {
            getAllNotesQuery.mutate().catch((err) => window.console.log(err));
            ShowSuccess(tCommon('actionsSuccessfulToast'));
            SubjectMonitoringService.refreshSubjectObservationsGrid();
        } else {
            ShowError(tCommon('errorMessage'));
        }
        setShowConvertDialog(false);
        setNoteToEdit(undefined);
    };

    const onRemovedFromReview = () => {
        setShowAddNoteDialog(false);
        smActiveReviewContext?.dispatch({ type: 'CLEAR_ACTIVE_REVIEW' });
    };

    const onSubmitHandler = () => {
        getAllNotesQuery.mutate().catch((err) => window.console.log(err));
        setShowAddNoteDialog(false);
    };

    const onCloseHandler = () => {
        setShowAddNoteDialog(false);
    };

    useEffect(() => {
        const subscribeDefault = SubjectProfileService.onRefreshNotesGrid().subscribe((refresh) => {
            if (refresh) {
                getAllNotesQuery.mutate().catch((err) => window.console.log(err));
            }
        });
        return () => subscribeDefault.unsubscribe();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    return (
        <>
            {showAddNoteDialog && (
                <AddSubjectNoteDialog
                    sessionId={sessionId}
                    studyRef={studyRef}
                    subjectId={subjectId}
                    subjectNumber={subjectNumber}
                    datasetId={datasetId}
                    datasetOpenedOn={datasetOpenedOn}
                    reviewId={activeReview?.id ?? undefined}
                    reviewTitle={activeReview?.title ?? undefined}
                    reviewDatasetDate={activeReview?.datasetDate ?? undefined}
                    associationName="Subject Profile"
                    associationType={SubjectAssociationTypes.SubjectProfile}
                    dataListingName={null}
                    dataListingRef={null}
                    panelName={null}
                    panelRef={null}
                    onClose={onCloseHandler}
                    onSubmit={onSubmitHandler}
                    onRemovedFromReview={onRemovedFromReview}
                />
            )}
            {showEditNoteDialog && noteToEdit && (
                <EditSubjectNoteDialog
                    studyRef={studyRef}
                    note={noteToEdit}
                    onClose={onEditNoteClose}
                    onSubmit={onEditNoteSubmit}
                    onConvert={(observationText) => onConvertToObservation(observationText)}
                />
            )}
            {showConvertDialog && noteToEdit && (
                <EditSmObservationDialog
                    studyRef={studyRef}
                    observationToEdit={mapNoteToObservationGridRow(noteToEdit)}
                    convertToManual
                    onClose={onConvertClose}
                    onSubmit={onConvertSubmit}
                />
            )}
            <Grid
                dataItemKey="all-subject-notes-grid"
                data={getAllNotesQuery.data}
                columns={columns}
                title={tSm('grid.title')}
                testDataId="all-subject-notes-grid"
                maxHeight="auto"
                gridDownloadDisabled
                addCallback={() => setShowAddNoteDialog(true)}
                canEdit={canEdit}
                rowMenuActions={noteMenuActions}
            />
        </>
    );
};

export default SubjectMonitoringNotesGrid;
